import React from 'react';
import { IPublicMaterialPricing, IPublicPricing } from '../../services/pricing_public/pricing_public';
import { biggestSaving, money, WallEstimate, WallPreset } from './earnings';
import { StepItem, StepList } from './StepList.component';

interface Props {
    pricing: IPublicPricing;
    steps: StepItem[];
    /** The wall and material picked in the calculator, for the worked example. */
    wall: WallPreset;
    material: IPublicMaterialPricing;
    estimate: WallEstimate;
    ordersPerMonth: number;
    /** True once the brand has a Shopify store connected, so the tier is already on. */
    unlocked?: boolean;
    /** The call to action under the steps; omitted when there is none. */
    action?: React.ReactNode;
    /** Tailwind classes that lay the table beside the steps while the panel is full width. */
    columns: string;
}

// #region BRAND_STEPS
/** The Brand path, in order. Callers add routes and done flags where they know them. */
export const BRAND_STEPS: StepItem[] = [
    { label: 'Connect your Shopify store or the Wallmates API' },
    { label: 'Your next orders are printed at Store Owner rates' },
    { label: 'Keep your store or API connection active to keep the lower rates' }
];

// #region StoreOwnerUnlock
/** The Brand side panel: what printing costs with and without a store, a worked example, and the steps. */
export const StoreOwnerUnlock: React.FunctionComponent<Props> = ({ pricing, steps, wall, material, estimate, ordersPerMonth, unlocked, action, columns }): React.ReactElement => {
    const saving = estimate.artistCost - estimate.storeOwnerCost;
    return (
        <div>
            <p className="text-xs font-semibold uppercase tracking-wider text-brand">For brands</p>
            <h3 className="mt-1 font-display text-2xl font-semibold tracking-tight text-black dark:text-white">
                {unlocked ? 'You pay Store Owner rates' : `Connect Shopify or our API, pay up to ${biggestSaving(pricing.materials)}% less`}
            </h3>
            <p className="mt-1 text-sm text-zinc-600 dark:text-zinc-400">
                Wallmates prints and ships every order for you. With a Shopify store or our API connected, you pay a lower rate for that printing. It&rsquo;s free and needs no approval.
            </p>

            <div className={`mt-5 grid gap-x-10 gap-y-5 ${columns}`}>
                <div>
                    <table className="w-full text-sm tabular-nums">
                        <caption className="mb-2 text-left text-sm font-semibold text-zinc-900 dark:text-zinc-100">What you pay to print, per sq ft</caption>
                        <thead>
                            <tr className="text-xs text-zinc-500 dark:text-zinc-400">
                                <th scope="col" className="pb-2 text-left font-medium" />
                                <th scope="col" className="pb-2 text-right font-medium">No store<span className="block font-normal">(Artists)</span></th>
                                <th scope="col" className="pb-2 text-right font-medium">With Shopify<span className="block font-normal">/ API</span></th>
                                <th scope="col" className="pb-2 align-top text-right font-medium">You save</th>
                            </tr>
                        </thead>
                        <tbody className="divide-y divide-surface-border border-t border-surface-border">
                            {pricing.materials.map((m) => (
                                <tr key={m.key} className={m.key === material.key ? 'bg-seabreeze/30 dark:bg-seabreeze/10' : ''}>
                                    <th scope="row" className="py-2 pl-2 text-left font-medium text-zinc-800 dark:text-zinc-200">{m.label}</th>
                                    <td className="py-2 text-right text-zinc-500 line-through decoration-zinc-400 dark:text-zinc-400">{money(m.artist_cost_sqft)}</td>
                                    <td className="py-2 text-right font-semibold text-zinc-900 dark:text-zinc-100">{money(m.store_owner_cost_sqft)}</td>
                                    <td className="py-2 pr-2 text-right font-semibold text-blue-600 dark:text-blue-400">{percentOff(m)}%</td>
                                </tr>
                            ))}
                        </tbody>
                    </table>

                    <div className="mt-4 rounded-xl bg-lavender/40 px-4 py-3 text-sm text-zinc-800 dark:bg-lavender-deep/20 dark:text-zinc-200">
                        <p className="text-xs font-semibold uppercase tracking-wider text-lavender-deep dark:text-lavender">Example</p>
                        <p className="mt-1">
                            A {wall.widthFt} × {wall.heightFt} ft wall in {material.label} costs{' '}
                            <span className="whitespace-nowrap tabular-nums line-through decoration-zinc-400">{money(estimate.artistCost)}</span> to print without a store, or{' '}
                            <span className="whitespace-nowrap font-semibold tabular-nums text-zinc-900 dark:text-zinc-100">{money(estimate.storeOwnerCost)}</span> with one.
                        </p>
                        <p className="mt-1 font-semibold text-black dark:text-white">
                            You keep {money(saving)} more per order, {money(saving * ordersPerMonth, false)} a month at {ordersPerMonth} {ordersPerMonth === 1 ? 'order' : 'orders'}.
                        </p>
                    </div>
                </div>

                <div>
                    <p className="mb-3 text-sm font-semibold text-zinc-900 dark:text-zinc-100">How to get it</p>
                    <StepList steps={steps} />
                    {action ? <div className="mt-5">{action}</div> : null}
                </div>
            </div>
        </div>
    );
};

const percentOff = (m: IPublicMaterialPricing): number =>
    m.artist_cost_sqft > 0 ? Math.round(((m.artist_cost_sqft - m.store_owner_cost_sqft) / m.artist_cost_sqft) * 100) : 0;
